import { useState } from "react";
import { Copy, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { absUrl } from "@/lib/api";

/**
 * Compact shareable card for both death and victory outcomes.
 * Renders inside GameOverScreen / VictoryScreen, and standalone on /card/:id.
 */
export default function DeathCard({ card, embedded = false, livesRemaining = null }) {
  const [copied, setCopied] = useState(false);
  if (!card) return null;

  const isVictory = card.outcome === "victory";
  // Build the shareable URL with UTM attribution so analytics can measure viral acquisition.
  const utmParams = new URLSearchParams({
    utm_source: "share",
    utm_medium: "card",
    utm_campaign: `${card.class_key || "hero"}_${card.outcome || "run"}`,
  }).toString();
  const url = `${window.location.origin}/card/${card.share_id}?${utmParams}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — link is: " + url);
    }
  };

  const nativeShare = async () => {
    const shareData = {
      title: isVictory
        ? `${card.user_name} slew the Ember King on floor ${card.floors_cleared}.`
        : `${card.user_name} fell on floor ${card.floors_cleared} of Champions of the Dawn Vanguard.`,
      text: card.final_narrative,
      url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* dismissed */ }
    } else {
      copyLink();
    }
  };

  return (
    <div
      className={`panel overflow-hidden ${embedded ? "" : "max-w-2xl mx-auto"}`}
      data-testid="death-card"
      style={{
        borderColor: isVictory ? "rgba(212,175,55,0.5)" : "rgba(138,3,3,0.5)",
        boxShadow: isVictory ? "0 0 40px rgba(212,175,55,0.25)" : "0 0 40px rgba(138,3,3,0.25)",
      }}
    >
      {/* Header stripe */}
      <div className={`px-6 py-2 text-[10px] tracking-[0.5em] ${isVictory ? "gold-text" : "blood-text"} flex items-center justify-center gap-2`}>
        {isVictory ? "◆ THE EMBER RETURNS ◆" : "🔥 FALLEN IN THE ASHES 🔥"}
      </div>

      {/* Structured Hall of Ashes summary — spec-compliant, scannable in < 2s */}
      {!isVictory && (
        <div className="px-6 pb-3 pt-1 border-b" style={{ borderColor: "rgba(138,3,3,0.35)" }} data-testid="card-hall-summary">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-[11px] tracking-wider">
            <div><span className="ash-text opacity-70">PLAYER:</span> <span className="text-[#f0e6d2]">{card.user_name}</span></div>
            <div><span className="ash-text opacity-70">LOCATION:</span> <span className="text-[#f0e6d2]">Floor {card.floors_cleared} of 20</span></div>
            <div><span className="ash-text opacity-70">SLAIN BY:</span> <span className="text-[#ff7a66]">{card.final_enemy || "The dark"}</span></div>
            <div>
              <span className="ash-text opacity-70">DAILY STATUS:</span>{" "}
              <span className="ember">{livesRemaining ?? "?"}/3 Lives Left Today</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
        {/* Portrait */}
        <div className="aspect-[3/4] md:aspect-auto md:h-full bg-black relative">
          {card.portrait_url ? (
            <img src={absUrl(card.portrait_url)} alt={card.class_name} className="w-full h-full object-cover" data-testid="card-portrait" />
          ) : (
            <div className="w-full h-full skeleton" />
          )}
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 60px rgba(0,0,0,0.7)" }} />
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/95 to-transparent">
            <div className="text-[10px] tracking-[0.3em] ash-text">CLASS</div>
            <div className="font-heading text-xl gold-text" data-testid="card-class">{card.class_name}</div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="text-[10px] tracking-[0.3em] ash-text">
            {isVictory ? "VICTOR" : "FALLEN HERO"}
          </div>
          <div className="font-heading text-3xl text-[#f0e6d2] mt-1 italic" data-testid="card-user">
            {card.user_name}
          </div>
          <div className="text-xs ash-text tracking-widest mt-2">
            {card.chapter && <>CHAPTER {card.chapter} · </>}{card.chapter_title}
          </div>

          <p className="font-narrative text-base ash-text italic mt-4 leading-relaxed" data-testid="card-narrative">
            <span className="text-[10px] tracking-[0.3em] not-italic block mb-1 opacity-70">FINAL WORDS · LAST CHOICE</span>
            &ldquo;{(card.final_narrative || "The dungeon keeps its silence.").replace(/\*\*|__|\*/g, "")}&rdquo;
          </p>

          <div className="grid grid-cols-4 gap-3 mt-5 text-center">
            <div>
              <div className="text-[9px] tracking-[0.3em] ash-text">FLOOR</div>
              <div className="font-heading text-2xl arcane-text" data-testid="card-floor">{card.floors_cleared}</div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.3em] ash-text">LEVEL</div>
              <div className="font-heading text-2xl gold-text" data-testid="card-level">{card.final_level}</div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.3em] ash-text">GOLD</div>
              <div className="font-heading text-2xl gold-text">{card.gold}</div>
            </div>
            <div>
              <div className="text-[9px] tracking-[0.3em] ash-text">TURNS</div>
              <div className="font-heading text-2xl text-[#f0e6d2]">{card.final_turn}</div>
            </div>
          </div>

          {card.final_enemy && !isVictory && (
            <div className="mt-4 text-xs ash-text italic">
              Slain by <span className="blood-text not-italic">{card.final_enemy}</span>.
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button onClick={copyLink} data-testid="card-copy-btn" className="btn-glow flex items-center gap-2 text-xs">
              {copied ? <><Check size={14} /> COPIED</> : <><Copy size={14} /> COPY LINK</>}
            </button>
            <button onClick={nativeShare} data-testid="card-share-btn" className="btn-primary flex items-center gap-2 text-xs">
              <Share2 size={14} /> SHARE
            </button>
            <div className="text-[10px] ash-text tracking-widest ml-auto">/card/{card.share_id}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
